import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { 
  Megaphone, 
  Storefront, 
  Users, 
  Target, 
  SignOut,
  List,
  GearSix,
  ChartBar,
  X,
  GameController,
  Trophy,
  ClockCounterClockwise,
  CaretDown,
  Calculator,
  Wrench,
  Tag,
  ArrowsLeftRight,
  MapPin,
  Lightning,
  Table,
  UserList,
  Palette,
  ShieldCheck,
  House as HouseIcon,
  CaretRight,
  Sun,
  Moon,
  Chalkboard,
  CalendarBlank,
  Layout,
  CurrencyDollar,
  Cube,
  Sparkle,
  ArrowRight,
  Scales,
  BookOpen,
  Phone,
  BellRinging,
  BellSlash,
  Bell,
  User,
  NotePencil,
  Camera,
  ClipboardText,
  HandCoins,
  Vault,
  Umbrella,
  AddressBook,
  MagnifyingGlass,
  Newspaper,
  ArrowLeft,
  QrCode,
  BookBookmark,
  Broadcast,
  Briefcase,
  Crown,
} from '@phosphor-icons/react';

// ─── STATIC IMPORTS — only chrome that always renders on every page ───
// (header, drawer trigger, ambient bg, bottom tab bar).  Every route +
// settings panel is lazy-loaded below so the initial dashboard chunk
// stays tiny and iPhone Safari doesn't try to parse 1.2 MB on cold start.
import Announcements from '../components/Announcements';      // tiny, in-header
import ConnectionSourceLogo from '../components/ConnectionSourceLogo';
import EdgeSwipeCatcher from '../components/EdgeSwipeCatcher';
import InstallPrompt from '../components/InstallPrompt';
import RouteErrorBoundary from '../components/RouteErrorBoundary';
import AppAmbient from '../components/AppAmbient';
import BottomTabBar from '../components/FloatingTabBar';
import RouteSkeleton from '../components/RouteSkeleton';
import CommandPalette from '../components/CommandPalette';
import SideNav from '../components/SideNav';
import FavTabPicker from '../components/FavTabPicker';
import useSwipeNav from '../hooks/useSwipeNav';
import usePageVisibility from '../hooks/usePageVisibility';
import useAppUpdateChecker from '../hooks/useAppUpdateChecker';
import useRecentsAndPins from '../hooks/useRecentsAndPins';
import NotificationBell from '../notifications/NotificationBell';
import NotificationDrawer from '../notifications/NotificationDrawer';

// ─── LAZY ROUTE COMPONENTS — fetched on first visit, cached after ───
const HomeDashboard          = lazy(() => import('../components/HomeDashboard'));
const StoresView             = lazy(() => import('../components/StoresView'));
const RepsView               = lazy(() => import('../components/RepsView'));
const GoalsView              = lazy(() => import('../components/GoalsView'));
const Top10View              = lazy(() => import('../components/Top10View'));
const StateOfBusiness        = lazy(() => import('../components/StateOfBusiness'));
const ProfitabilityMatrix    = lazy(() => import('../components/ProfitabilityMatrix'));
const DeviceSpotlightPreview = lazy(() => import('../components/DeviceSpotlightPreview'));
const PlansCalculator        = lazy(() => import('../components/PlansCalculator'));
const BMSMPromos             = lazy(() => import('../components/BMSMPromos'));
const PortingDirectory       = lazy(() => import('../components/PortingDirectory'));
const LocationsView          = lazy(() => import('../components/LocationsView'));
const PerksView              = lazy(() => import('../components/PerksView'));
const VHDPCalculator         = lazy(() => import('../components/VHDPCalculator'));
const VHIClosing             = lazy(() => import('../components/VHIClosing'));
const Forward                = lazy(() => import('../components/forward/Forward'));
const PaycheckView           = lazy(() => import('../components/PaycheckView'));
const CoachingDashboard      = lazy(() => import('../components/CoachingDashboard'));
const CalendarView           = lazy(() => import('../components/CalendarView'));
const InventoryVault         = lazy(() => import('../components/InventoryVault'));
const Reconciliation         = lazy(() => import('../components/Reconciliation'));
const SalesPitches           = lazy(() => import('../components/SalesPitches'));
const CallScripts            = lazy(() => import('../components/CallScripts'));
const NotesView              = lazy(() => import('../components/NotesView'));
const ContactDirectory       = lazy(() => import('../components/ContactDirectory'));
const VerizonNews            = lazy(() => import('../components/VerizonNews'));
const SimplicityHub          = lazy(() => import('../components/SimplicityHub'));
const QRReviewsView          = lazy(() => import('../components/QRReviewsView'));
const DailiesView            = lazy(() => import('../components/DailiesView'));
const AuditPage              = lazy(() => import('../components/AuditPage'));
const RepProfile             = lazy(() => import('../components/ProfilePage').then(m => ({ default: m.RepProfile })));
const StoreProfile           = lazy(() => import('../components/ProfilePage').then(m => ({ default: m.StoreProfile })));
const CompanyHandbook        = lazy(() => import('./CompanyHandbook'));
const BuyPhones              = lazy(() => import('./BuyPhones'));
const SignalCheck            = lazy(() => import('./SignalCheck'));
const BusinessFunnel         = lazy(() => import('./BusinessFunnel'));

// ─── LAZY GAMES — full Battleship + Bingo trees (huge, rarely opened) ───
const BattleshipLanding      = lazy(() => import('../components/games/battleship/BattleshipLanding'));
const BattleshipGameBoard    = lazy(() => import('../components/games/battleship/BattleshipGameBoard'));
const BattleshipFireShot     = lazy(() => import('../components/games/battleship/BattleshipFireShot'));
const BattleshipTeamPortal   = lazy(() => import('../components/games/battleship/BattleshipTeamPortal'));
const BattleshipAdmin        = lazy(() => import('../components/games/battleship/BattleshipAdmin'));
const BattleshipLeaderboard  = lazy(() => import('../components/games/battleship/BattleshipLeaderboard'));
const BattleshipArchives     = lazy(() => import('../components/games/battleship/BattleshipArchives'));
const BingoHome              = lazy(() => import('../components/games/bingo/BingoHome'));
const BingoAdmin             = lazy(() => import('../components/games/bingo/BingoAdmin'));
const BingoHistory           = lazy(() => import('../components/games/bingo/BingoHistory'));
const BingoArchives          = lazy(() => import('../components/games/bingo/BingoArchives'));
const ConquestHome           = lazy(() => import('../components/games/conquest/ConquestHome'));
const ConquestAdmin          = lazy(() => import('../components/games/conquest/ConquestAdmin'));
const ConquestHistory        = lazy(() => import('../components/games/conquest/ConquestHistory'));
const RingerHome             = lazy(() => import('../components/games/ringer/RingerHome'));
const RingerAdmin            = lazy(() => import('../components/games/ringer/RingerAdmin'));

// ─── LAZY SETTINGS PANELS — only the open tab loads ───
const SheetsManager          = lazy(() => import('../components/SheetsManager'));
const AnnouncementsManager   = lazy(() => import('../components/AnnouncementsManager'));
const Top10Manager           = lazy(() => import('../components/Top10Manager'));
const StoreAliasManager      = lazy(() => import('../components/StoreAliasManager'));
const GoalAliasManager       = lazy(() => import('../components/GoalAliasManager'));
const GoalsManager           = lazy(() => import('../components/GoalsManager'));
const WhitelistManager       = lazy(() => import('../components/WhitelistManager'));
const CardDisplaySettings    = lazy(() => import('../components/CardDisplaySettings'));
const PBIManager             = lazy(() => import('../components/PBIManager'));
const RepsSettings           = lazy(() => import('../components/RepsSettings'));
const StoresSettings         = lazy(() => import('../components/StoresSettings'));
const LocationsSettings      = lazy(() => import('../components/LocationsSettings'));
const PerksSettings          = lazy(() => import('../components/PerksSettings'));
const ContactSettings        = lazy(() => import('../components/ContactSettings'));
const CoachingSettings       = lazy(() => import('../components/CoachingSettings'));
const CalendarSettings       = lazy(() => import('../components/CalendarSettings'));
const VHDPSettings           = lazy(() => import('../components/VHDPSettings'));
const PaycheckSettings       = lazy(() => import('../components/PaycheckSettings'));
const ProfileSettings        = lazy(() => import('../components/ProfileSettings'));
const InventoryVaultSettings = lazy(() => import('../components/InventoryVaultSettings'));
const ReconciliationSettings = lazy(() => import('../components/ReconciliationSettings'));
const NotificationSettings   = lazy(() => import('../components/NotificationSettings'));
const SnapshotManager        = lazy(() => import('../components/SnapshotManager'));
const OnboardingSubmissions  = lazy(() => import('../components/OnboardingSubmissions'));
const DailiesSettings        = lazy(() => import('../components/DailiesSettings'));
const PTORequest             = lazy(() => import('../components/PTORequest'));
const PTOSettings            = lazy(() => import('../components/PTOSettings'));
const QRReviewsSettings      = lazy(() => import('../components/QRReviewsSettings'));
// Legacy NotificationCenter retained behind admin Settings for the old
// preference UI; the in-app inbox is now driven by NotificationDrawer
// + NotificationBell.
const NotificationCenter     = lazy(() => import('../components/NotificationCenter'));
// MissingCaptureBanner is rendered inside Settings → Snapshots, not on the dashboard.

// Lazy-loaded — only fetched when a mobile user actually opens the drawer.
// We ALSO trigger the import on idle (see useEffect below) so that the
// first drawer open is instant rather than waiting on the chunk fetch.
const MobileDrawer = lazy(() => import('../components/PocketDrawer'));
const MobileDrawerSkeleton = lazy(() => import('../components/MobileDrawerSkeleton'));

// Kick off the drawer chunk fetch on browser idle so the first tap is instant.
// `requestIdleCallback` defers until the main thread is free; on Safari (no
// IDLE API) we fall back to a 1.5s setTimeout — far enough to not compete
// with the dashboard's initial render, close enough that the chunk lands
// before the user reaches for the menu icon.
if (typeof window !== 'undefined') {
  const preload = () => {
    import('../components/PocketDrawer').catch(() => {});
    // Pre-warm the routes a freshly-logged-in user is most likely to open
    // next.  These are the four tabs in the bottom bar.  Chunk cost is
    // tiny because they share a vendor chunk with HomeDashboard.
    import('../components/StoresView').catch(() => {});
    import('../components/RepsView').catch(() => {});
    import('../components/Top10View').catch(() => {});
    import('../components/GoalsView').catch(() => {});
  };
  // On iOS mobile Safari, defer further — initial-load memory pressure
  // can crash the tab when 5 chunks land in parallel during render.
  const isIosMobile = typeof navigator !== 'undefined'
    && /iPad|iPhone|iPod/.test(navigator.userAgent)
    && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
  const fallbackDelay = isIosMobile ? 4500 : 1500;
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(preload, { timeout: 5000 });
  } else {
    setTimeout(preload, fallbackDelay);
  }
}

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── CURATED CATEGORY PALETTE ────────────────────────────────────
   6 hues, each with semantic meaning:
     red    → primary nav + chrome accent
     emerald→ money / metrics / fulfillment
     sunset → sales / customer-facing tools
     iris   → wins / games / leaderboard
     steel  → reference / info / data
     slate  → utility / default
   Items inherit their category color unless explicitly overridden. */
const PALETTE = {
  red:     '#CD040B',
  emerald: '#10B981',
  sunset:  '#F97316',
  iris:    '#A855F7',
  steel:   '#3B82F6',
  slate:   '#A3A3A3',
};
const NAV_COLORS = {
  // Primary nav — all brand red except Top 10 (the "winners" tab → iris).
  'Dashboard': PALETTE.red,
  'My Home': PALETTE.red,
  'Stores': PALETTE.red,
  'Reps': PALETTE.red,
  'Goals': PALETTE.red,
  'Top 10': PALETTE.iris,
  // Calculators — sunset (sales/customer).
  'Plan Calculator': PALETTE.sunset,
  'Perks Calculator': PALETTE.sunset,
  'VHDP Calculator': PALETTE.sunset,
  'Commission Estimator': PALETTE.emerald,
  // Sales Resources — sunset.
  'Sales Pitches': PALETTE.sunset,
  'Call Scripts': PALETTE.sunset,
  'My Notes': PALETTE.sunset,
  'Device Pricing': PALETTE.emerald,
  'Porting Guide': PALETTE.sunset,
  'Contact Directory': PALETTE.steel,
  'Buy Phones': PALETTE.amber,
  'Verizon News': PALETTE.steel,
  'Discover Daily': PALETTE.red,
  // Management & Info — steel (read-only data) / emerald (money flows).
  'Coaching': PALETTE.red,
  'Work Schedules': PALETTE.steel,
  'Inventory Vault': PALETTE.steel,
  'Reconciliation': PALETTE.emerald,
  'Location Info': PALETTE.steel,
  'Dailies': PALETTE.emerald,
  'Self-Audit': PALETTE.red,
  'PTO Request': PALETTE.steel,
  'Company Handbook': PALETTE.steel,
  'Signal Check': PALETTE.iris,
  'Business Funnel': PALETTE.iris,
  'Discretionary Funds': PALETTE.emerald,
  // Games — iris (fun / winners).
  'Bingo': PALETTE.iris,
  'Sales Conquest': PALETTE.teal || '#1ED6C3',
  'Battleship': PALETTE.iris,
  'BMSM Promos': PALETTE.red,
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Keep long-lived tabs auto-aligned with the latest deploy.  Hook is
  // visibility-gated (zero cost on backgrounded tabs) and reloads only
  // when the user is idle so we never interrupt typing or active gestures.
  useAppUpdateChecker();
  const [showSettings, setShowSettings] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [toolsSearch, setToolsSearch] = useState('');
  const toolsSearchRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const megaMenuRef = useRef(null);
  const toolsBtnRef = useRef(null);
  const [settingsTab, setSettingsTab] = useState('whitelist');
  const [settingsSidebarOpen, setSettingsSidebarOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showGamesMenu, setShowGamesMenu] = useState(false);
  const gamesBtnRef = useRef(null);
  const gamesMenuRef = useRef(null);

  // Theme state - persisted per user in localStorage
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(`theme_${user?.email}`);
    return stored || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (user?.email) {
      localStorage.setItem(`theme_${user.email}`, theme);
    }
  }, [theme, user?.email]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  const isDark = theme === 'dark';
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [showSyncDetails, setShowSyncDetails] = useState(false);

  // Fetch last sync time
  const fetchLastSync = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/last-sync`);
      setLastSync(data);
    } catch (e) { console.error('Failed to fetch sync time:', e); }
  }, []);

  useEffect(() => {
    fetchLastSync();
  }, [fetchLastSync]);

  // Page-visibility-gated polling: only refresh while the tab is in the
  // foreground.  Prevents background iOS Safari tabs from queuing up
  // burst-fires of stale syncs when the user returns to the app.
  const pageVisible = usePageVisibility();
  useEffect(() => {
    if (!pageVisible) return;
    const interval = setInterval(fetchLastSync, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchLastSync, pageVisible]);

  // Close sync dropdown on outside click
  useEffect(() => {
    if (!showSyncDetails) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="last-sync-btn"]') && !e.target.closest('[data-testid="sync-details-dropdown"]')) {
        setShowSyncDetails(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showSyncDetails]);

  useEffect(() => {
    if (!showToolsMenu) {
      setToolsSearch('');
      return;
    }
    // Focus the search input as soon as the menu opens
    setTimeout(() => toolsSearchRef.current?.focus(), 50);
    const handler = (e) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target) && 
          toolsBtnRef.current && !toolsBtnRef.current.contains(e.target)) {
        setShowToolsMenu(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showToolsMenu]);

  useEffect(() => {
    if (!showGamesMenu) return;
    const handler = (e) => {
      if (gamesMenuRef.current && !gamesMenuRef.current.contains(e.target) &&
          gamesBtnRef.current && !gamesBtnRef.current.contains(e.target)) {
        setShowGamesMenu(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showGamesMenu]);

  // Check if any sync time is stale (over 2 hours old)
  const isSyncStale = useMemo(() => {
    if (!lastSync?.all_syncs?.length) return false;
    const now = new Date();
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    return lastSync.all_syncs.some(s => {
      if (!s.iso) return false;
      const syncDate = new Date(s.iso);
      return (now - syncDate) > TWO_HOURS;
    });
  }, [lastSync]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const canAccessSettings = user?.settings_access === true;

  const handleSettingsClick = () => {
    const sections = user?.settings_sections || [];
    if (sections.length > 0 && !sections.includes(settingsTab)) {
      setSettingsTab(sections[0]);
    }
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
    setRefreshKey(k => k + 1);
    fetchLastSync();
  };

  const navItems = [
    { path: '/me', icon: Layout, label: 'Dashboard', exact: true },
    { path: '/stores', icon: Storefront, label: 'Stores' },
    { path: '/reps', icon: Users, label: 'Reps' },
    { path: '/top10', icon: Trophy, label: 'Top 10' },
    { path: '/goals', icon: Target, label: 'Goals' },
  ];

  /* ─── Mobile bottom-bar slots — all 5 slots customizable, per-device.
     Declared early because `useSwipeNav` below subscribes to the first 4. ─── */
  const SLOTS_KEY = 'cs.mobileTabSlots';
  const DEFAULT_SLOTS = ['/me', '/stores', '/reps', '/top10', '/profitability'];
  const readSlots = () => {
    try {
      const raw = localStorage.getItem(SLOTS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length === 5 && parsed.every(p => typeof p === 'string')) {
          return parsed;
        }
      }
      const old = localStorage.getItem('cs.favMobileTab');
      if (old) {
        const slots = [...DEFAULT_SLOTS];
        slots[4] = old;
        try { localStorage.setItem(SLOTS_KEY, JSON.stringify(slots)); } catch { /* quota */ }
        try { localStorage.removeItem('cs.favMobileTab'); } catch { /* noop */ }
        return slots;
      }
    } catch { /* ignore */ }
    return DEFAULT_SLOTS;
  };
  const [slotPaths, setSlotPaths] = useState(readSlots);
  const [activeSlotIdx, setActiveSlotIdx] = useState(null);
  const handleOpenSlotPicker = useCallback((idx) => { setActiveSlotIdx(idx); }, []);
  const handleSelectSlot = useCallback((path) => {
    setActiveSlotIdx(idx => {
      if (idx == null) return null;
      setSlotPaths(prev => {
        const next = [...prev];
        next[idx] = path;
        try { localStorage.setItem(SLOTS_KEY, JSON.stringify(next)); } catch { /* quota */ }
        return next;
      });
      return null;
    });
  }, []);

  // Wire mobile swipe-left/right to navigate between the four destinations
  // that appear in the bottom tab bar.  `/goals` is omitted from the swipe
  // ring because it's accessed via the More drawer on mobile — keeping the
  // swipe in lock-step with the visible tabs avoids the "swiped to a tab I
  // can't see" surprise.
  useSwipeNav(slotPaths.slice(0, 4));

  const gamesItems = [
    { path: '/bingo', label: 'Bingo', icon: Sparkle, desc: 'Sales Bingo game', color: '#EC4899' },
    { path: '/battleship', label: 'Battleship', icon: Target, desc: 'Sales battleship game', color: '#CD040B' },
    { path: '/conquest', label: 'Sales Conquest', icon: Crown, desc: 'Hex-map territory game', color: '#1ED6C3' },
    { path: '/ringer', label: 'Ringer Arcade', icon: GameController, desc: 'Sales-floor prize wheel', color: '#FF2E88' },
  ];

  const gamesActive = location.pathname.startsWith('/battleship') || location.pathname.startsWith('/bingo') || location.pathname.startsWith('/conquest') || location.pathname.startsWith('/ringer');

  const toolsCategories = useMemo(() => [
    {
      label: 'Calculators',
      color: PALETTE.sunset,
      items: [
        { path: '/plans', icon: Calculator, label: 'Plan Calculator', desc: 'Compare line pricing' },
        { path: '/perks', icon: Lightning, label: 'Perks Calculator', desc: 'Streaming bundles' },
        { path: '/vhdp', icon: ShieldCheck, label: 'VHDP Calculator', desc: 'Device home value' },
        { path: '/vhi-closer', icon: HouseIcon, label: 'Internet Closer', desc: 'VHI · Fios · Frontier sales console' },
        ...((user?.role === 'admin') ? [{ path: '/paychecks', icon: CurrencyDollar, label: 'Commission Estimator', desc: 'Earnings breakdown' }] : []),
      ],
    },
    {
      label: 'Sales Resources',
      color: PALETTE.sunset,
      items: [
        { path: '/sales-pitches', icon: BookOpen, label: 'Sales Pitches', desc: '10K pitch database' },
        { path: '/call-scripts', icon: Phone, label: 'Call Scripts', desc: '1K call script library' },
        { path: '/notes', icon: NotePencil, label: 'My Notes', desc: 'Personal notes library' },
        { path: '/profitability', icon: ChartBar, label: 'Device Pricing', desc: 'Margin & profit data' },
        { path: '/porting', icon: ArrowsLeftRight, label: 'Porting Guide', desc: 'Carrier port numbers' },
        { path: '/contacts', icon: Phone, label: 'Contact Directory', desc: 'Important phone numbers' },
        { path: '/simplicity-hub', icon: Sparkle, label: 'Simplicity Hub', desc: 'Verizon Simplicity plan reference' },
        { path: '/qr-reviews', icon: QrCode, label: 'Review QRs', desc: 'Per-store Google review QR codes' },
        { path: '/buy-phones', icon: Phone, label: 'Buy Phones', desc: 'Log trade-in / direct-buy devices' },
        { path: '/verizon-news', icon: Newspaper, label: 'Verizon News', desc: 'Latest from Verizon' },
        { path: 'https://discoverdaily.verizon.com/ui/rz/', icon: Megaphone, label: 'Discover Daily', desc: 'Verizon promos · opens in new tab', external: true },
      ],
    },
    {
      label: 'Management & Info',
      color: PALETTE.steel,
      items: [
        { path: '/coaching', icon: Chalkboard, label: 'Coaching', desc: 'Rep development' },
        { path: '/calendars', icon: CalendarBlank, label: 'Work Schedules', desc: 'Google calendars' },
        { path: '/inventory', icon: Cube, label: 'Inventory Vault', desc: 'Device stock lookup' },
        { path: '/reconciliation', icon: Scales, label: 'Reconciliation', desc: 'Billing reconcile' },
        { path: '/locations', icon: MapPin, label: 'Location Info', desc: 'Store directory' },
        { path: '/dailies', icon: Vault, label: 'Dailies', desc: 'Daily cash + Brinks deposits' },
        { path: '/audit', icon: ClipboardText, label: 'Self-Audit', desc: 'Monthly fleet excellence checklist' },
        { path: '/pto', icon: Umbrella, label: 'PTO Request', desc: 'Request vacation or sick time' },
        { path: '/handbook', icon: BookBookmark, label: 'Company Handbook', desc: 'Policies, leaves, benefits & state addenda' },
        { path: '/signal-check', icon: Broadcast, label: 'Signal Check', desc: 'MTD performance deck & scorecards' },
        { path: '/business-funnel', icon: Briefcase, label: 'Business Funnel', desc: 'Refer B2B leads to Bob & track in the CRM' },
        { path: 'https://docs.google.com/forms/d/e/1FAIpQLScafsEuGmcHgsix0jwJ71qS9SkwxK2Dk6gy4dMiDra68UyiRg/viewform?usp=dialog', icon: HandCoins, label: 'Discretionary Funds', desc: 'Submit a request', external: true },
      ],
    },
  ], [user?.role, user?.is_manager]);

  const allToolPaths = useMemo(() => toolsCategories.flatMap(c => c.items.map(i => i.path)), [toolsCategories]);
  const isToolsActive = allToolPaths.includes(location.pathname);

  /* ─── COMMAND PALETTE — keyboard-first universal navigator ─── */
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { recents, pins, isPinned, togglePin } = useRecentsAndPins(location.pathname);

  // Build a single flat registry so the palette can search every destination.
  const paletteTools = useMemo(() => {
    const out = [];
    // Primary nav
    navItems.forEach(n => out.push({
      path: n.path, label: n.label, icon: n.icon,
      desc: 'Primary navigation', category: 'Nav',
      color: NAV_COLORS[n.label] || PALETTE.red,
    }));
    // Tools categories
    toolsCategories.forEach(cat => cat.items.forEach(it => out.push({
      path: it.path, label: it.label, icon: it.icon, desc: it.desc,
      category: cat.label, external: !!it.external,
      color: NAV_COLORS[it.label] || cat.color,
    })));
    // Games
    gamesItems.forEach(g => out.push({
      path: g.path, label: g.label, icon: g.icon, desc: g.desc,
      category: 'Games', color: NAV_COLORS[g.label] || PALETTE.iris,
    }));
    return out;
  }, [navItems, toolsCategories, gamesItems]);

  // Global keyboard shortcut: ⌘K / Ctrl+K everywhere, plus "/" when no input is focused.
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      const meta = e.metaKey || e.ctrlKey;
      if (meta && k === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
        return;
      }
      if (k === '/' && !meta) {
        const el = document.activeElement;
        const tag = el?.tagName;
        const editable = tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable;
        if (!editable) { e.preventDefault(); setPaletteOpen(true); }
      }
      if (k === 'escape' && paletteOpen) setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen]);

  return (
    <div className="cs-dash-shell min-h-screen relative" style={{ background: 'var(--cs-bg)' }}>
      <AppAmbient />
      {/* God-tier sidebar — visible on lg+ only, self-positioned fixed. */}
      <SideNav
        navItems={navItems}
        toolsCategories={toolsCategories}
        gamesItems={gamesItems}
        navColors={NAV_COLORS}
        palette={PALETTE}
        user={user}
        paletteTools={paletteTools}
        pins={pins}
        isPinned={isPinned}
        togglePin={togglePin}
        onOpenPalette={() => setPaletteOpen(true)}
        onOpenSettings={handleSettingsClick}
        canAccessSettings={canAccessSettings}
        onLogout={handleLogout}
        onToggleTheme={toggleTheme}
        isDark={theme === 'dark'}
        onOpenNotifications={() => setNotifOpen(true)}
      />
      {/* Header */}
      <header className="nav-glass-premium sticky top-0 z-40 lg:hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="top-glow-bar" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 rounded-lg transition-colors hover:bg-[var(--cs-bg-hover)]" style={{ color: 'var(--cs-text)' }}
                onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
                data-testid="mobile-menu-btn"
              >
                <List size={24} />
              </button>
              <h1 className="logo-premium cursor-default lg:hidden" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <ConnectionSourceLogo size="md" />
              </h1>
            </div>

            <nav className="hidden items-center gap-1">
              {navItems.map(({ path, icon: Icon, label, exact, external }) => {
                const color = NAV_COLORS[label] || '#A3A3A3';
                return external ? (
                  <a
                    key={label}
                    href={path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-[var(--cs-bg-hover)]"
                    style={{ color: 'var(--cs-sidebar-text)' }}
                    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon size={18} />
                    {label}
                  </a>
                ) : (
                  <NavLink
                    key={path}
                    to={path}
                    end={exact}
                    className={({ isActive }) => 
                      `nav-item-premium flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-[10px] relative whitespace-nowrap ${
                        isActive ? '' : ''
                      }`
                    }
                    style={{ color: 'var(--cs-sidebar-text)' }}
                    data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon size={18} style={isActive ? { color, filter: `drop-shadow(0 0 6px ${color}60)` } : {}} className="transition-all duration-200" />
                        <span className="transition-colors duration-200" style={isActive ? { color } : {}}>{label}</span>
                        {isActive && (
                          <span className="nav-active-glow" style={{ background: color, boxShadow: `0 0 12px ${color}80` }} />
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
              {/* Tools mega-menu */}
              <div className="relative" data-testid="tools-menu">
                <button
                  ref={toolsBtnRef}
                  onClick={() => setShowToolsMenu(!showToolsMenu)}
                  className={`nav-item-premium flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-[10px] relative whitespace-nowrap`}
                  style={{ color: 'var(--cs-sidebar-text)' }}
                  data-testid="nav-tools"
                >
                  <Wrench size={18} style={isToolsActive ? { color: PALETTE.red, filter: 'drop-shadow(0 0 6px rgba(205,4,11,0.6))' } : {}} className="transition-all duration-200" />
                  <span className="transition-colors duration-200" style={isToolsActive ? { color: PALETTE.red } : {}}>Tools</span>
                  <CaretDown size={12} className={`transition-transform duration-300 ${showToolsMenu ? 'rotate-180' : ''}`} />
                  {isToolsActive && (
                    <span className="nav-active-glow" style={{ background: PALETTE.red, boxShadow: '0 0 12px rgba(205,4,11,0.8)' }} />
                  )}
                </button>
                <AnimatePresence>
                  {showToolsMenu && (() => {
                    // Flatten + filter tools by the search query
                    const q = toolsSearch.trim().toLowerCase();
                    const filteredCategories = q
                      ? toolsCategories.map(cat => ({
                          ...cat,
                          items: cat.items.filter(it =>
                            it.label.toLowerCase().includes(q) ||
                            (it.desc || '').toLowerCase().includes(q)
                          ),
                        })).filter(cat => cat.items.length > 0)
                      : toolsCategories;
                    const totalShown = filteredCategories.reduce((s, c) => s + c.items.length, 0);
                    return (
                    <motion.div
                      ref={megaMenuRef}
                      initial={{ opacity: 0, y: 12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.97 }}
                      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      className="mega-glass mega-menu-panel absolute top-full right-0 mt-3 rounded-2xl z-50 overflow-hidden flex flex-col"
                      style={{ width: 'min(820px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 120px)' }}
                      data-testid="tools-mega-menu"
                    >
                      {/* Ambient glow */}
                      <div className="ambient-glow" style={{ background: '#CD040B' }} />
                      {/* Top shimmer bar */}
                      <div className="top-glow-bar" />

                      {/* Search bar */}
                      <div className="px-4 py-3 border-b shrink-0" style={{ borderColor: 'var(--cs-border)', background: 'var(--cs-bg-surface)' }}>
                        <div className="relative">
                          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--cs-text-dim)' }} />
                          <input
                            ref={toolsSearchRef}
                            value={toolsSearch}
                            onChange={(e) => setToolsSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') { setShowToolsMenu(false); }
                              if (e.key === 'Enter' && filteredCategories[0]?.items[0]) {
                                const first = filteredCategories[0].items[0];
                                if (first.external) {
                                  window.open(first.path, '_blank', 'noopener,noreferrer');
                                } else {
                                  navigate(first.path);
                                }
                                setShowToolsMenu(false);
                              }
                            }}
                            placeholder="Search tools or press ⌘K…"
                            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border bg-transparent outline-none transition-all focus:border-[#CD040B]/40"
                            style={{ borderColor: 'var(--cs-border)', color: 'var(--cs-text)', background: 'var(--cs-bg-surface-2)' }}
                            data-testid="tools-search-input"
                          />
                          {toolsSearch && (
                            <button
                              onClick={() => setToolsSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center hover:bg-[var(--cs-bg-hover)]"
                              style={{ color: 'var(--cs-text-muted)' }}
                              data-testid="tools-search-clear"
                            >
                              <span className="text-xs">✕</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Category bands — each category gets a full-width band, items wrap into a grid */}
                      <div className="px-4 py-4 overflow-y-auto cs-hide-scrollbar flex-1">
                        {/* ─── Quick Access: pinned + recent — only when not searching ─── */}
                        {(!toolsSearch.trim()) && (pins.length > 0 || recents.length > 0) && (() => {
                          const byPath = new Map(paletteTools.map(t => [t.path, t]));
                          const pinned = pins.map(p => byPath.get(p)).filter(Boolean);
                          const rec = recents.map(p => byPath.get(p)).filter(Boolean)
                            .filter(it => !pins.includes(it.path)).slice(0, 5);
                          if (!pinned.length && !rec.length) return null;
                          const chip = (it, isPin) => {
                            const color = it.color || PALETTE.slate;
                            const Icon = it.icon;
                            return (
                              <NavLink
                                key={`${isPin ? 'p' : 'r'}-${it.path}`}
                                to={it.path}
                                onClick={() => setShowToolsMenu(false)}
                                className="group inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all hover:-translate-y-px"
                                style={{
                                  background: `linear-gradient(135deg, ${color}18, ${color}06)`,
                                  border: `1px solid ${color}30`,
                                  color: 'var(--cs-text)',
                                  fontFamily: 'Outfit, sans-serif',
                                  boxShadow: `0 4px 12px -6px ${color}40`,
                                }}
                                data-testid={`mega-quick-${it.path}`}>
                                {Icon && <Icon size={13} weight={isPin ? 'fill' : 'regular'} style={{ color }} />}
                                <span className="truncate max-w-[140px]">{it.label}</span>
                                {isPin && <span className="text-[9px] opacity-70" style={{ color: '#F59E0B' }}>★</span>}
                              </NavLink>
                            );
                          };
                          return (
                            <div className="mb-4" data-testid="mega-quick-access">
                              {pinned.length > 0 && (
                                <>
                                  <div className="mega-cat-label cat-label text-[10px] font-bold uppercase tracking-[0.15em] mb-2 px-1 flex items-center gap-2"
                                    style={{ color: 'var(--cs-text-muted)', fontFamily: 'Outfit, sans-serif' }}>
                                    <span>Pinned</span>
                                    <span className="text-[9px] font-mono opacity-60">{pinned.length}</span>
                                    <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, var(--cs-border), transparent)' }} />
                                  </div>
                                  <div className="flex flex-wrap gap-1.5 mb-3">{pinned.map(it => chip(it, true))}</div>
                                </>
                              )}
                              {rec.length > 0 && (
                                <>
                                  <div className="mega-cat-label cat-label text-[10px] font-bold uppercase tracking-[0.15em] mb-2 px-1 flex items-center gap-2"
                                    style={{ color: 'var(--cs-text-muted)', fontFamily: 'Outfit, sans-serif' }}>
                                    <span>Recent</span>
                                    <span className="text-[9px] font-mono opacity-60">{rec.length}</span>
                                    <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, var(--cs-border), transparent)' }} />
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">{rec.map(it => chip(it, false))}</div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                        {totalShown === 0 ? (
                          <div className="py-8 text-center text-sm text-[var(--cs-text-muted)]">
                            No tools match "{toolsSearch}"
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {filteredCategories.map((cat, catIdx) => (
                              <div key={cat.label} data-testid={`mega-cat-${cat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                                <div className="mega-cat-label cat-label text-[10px] font-bold uppercase tracking-[0.15em] mb-2 px-1 flex items-center gap-2"
                                  style={{ color: 'var(--cs-text-muted)', fontFamily: 'Outfit, sans-serif' }}>
                                  <span>{cat.label}</span>
                                  <span className="text-[9px] font-mono opacity-60">{cat.items.length}</span>
                                  <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, var(--cs-border), transparent)' }} />
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                  {cat.items.map((item, itemIdx) => {
                                    const isActive = location.pathname === item.path;
                                    const color = NAV_COLORS[item.label] || cat.color;
                                    const isExternal = !!item.external;
                                    const commonClass = "mega-tile mega-tile-premium group flex items-center gap-2 p-2 min-w-0";
                                    const commonStyle = { animationDelay: `${(catIdx * 4 + itemIdx) * 30}ms`, '--tile-glow': `${color}40` };
                                    const inner = (
                                      <>
                                        <div
                                          className="tile-icon-premium w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                          style={{ background: isActive ? `${color}20` : 'var(--cs-bg-hover)', color: isActive ? color : 'var(--cs-text-muted)', border: isActive ? `1px solid ${color}30` : '1px solid transparent' }}
                                        >
                                          <item.icon size={15} weight={isActive ? 'fill' : 'regular'} />
                                        </div>
                                        <div className="tile-text min-w-0 flex-1">
                                          <div className="mega-tile-title text-[12px] font-semibold leading-tight truncate" style={{ color: isActive ? color : 'var(--cs-text)', fontFamily: 'Outfit, sans-serif' }}>
                                            {item.label}
                                            {isExternal && <span className="ml-1 text-[8px] align-top opacity-60">↗</span>}
                                          </div>
                                          <div className="mega-tile-desc text-[10px] leading-snug truncate" style={{ color: 'var(--cs-text-dim)' }}>
                                            {item.desc}
                                          </div>
                                        </div>
                                        {isActive && <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />}
                                      </>
                                    );
                                    return isExternal ? (
                                      <a
                                        key={item.path}
                                        href={item.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`${item.label} — opens in a new tab`}
                                        onClick={() => setShowToolsMenu(false)}
                                        className={commonClass}
                                        style={commonStyle}
                                        data-testid={`mega-tool-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                      >
                                        {inner}
                                      </a>
                                    ) : (
                                      <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setShowToolsMenu(false)}
                                        className={commonClass}
                                        style={commonStyle}
                                        data-testid={`mega-tool-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                      >
                                        {inner}
                                      </NavLink>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Bottom promo strip */}
                      <div className="promo-card-bg border-t px-5 py-3 flex items-center justify-between shrink-0" style={{ borderColor: 'var(--cs-border)', background: 'linear-gradient(135deg, rgba(205, 4, 11, 0.06), transparent)' }}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#CD040B]/10 flex items-center justify-center">
                            <Sparkle size={16} weight="fill" className="text-[#CD040B]" />
                          </div>
                          <div>
                            <div className="promo-title text-xs font-bold" style={{ color: '#CD040B', fontFamily: 'Outfit, sans-serif' }}>Quick Access</div>
                            <div className="promo-desc text-[11px]" style={{ color: 'var(--cs-text-dim)' }}>Add tools as Dashboard widgets</div>
                          </div>
                        </div>
                        <NavLink
                          to="/"
                          onClick={() => setShowToolsMenu(false)}
                          className="flex items-center gap-1.5 text-[11px] font-semibold text-[#CD040B] hover:text-[#FF4B51] transition-colors"
                          data-testid="mega-menu-dashboard-link"
                        >
                          Go to Dashboard <ArrowRight size={12} weight="bold" />
                        </NavLink>
                      </div>
                    </motion.div>
                  ); })()}
                </AnimatePresence>
              </div>
              {/* Games dropdown menu */}
              <div className="relative" data-testid="games-menu">
                <button
                  ref={gamesBtnRef}
                  onClick={() => setShowGamesMenu(!showGamesMenu)}
                  className={`nav-item-premium flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-[10px] relative whitespace-nowrap`}
                  style={{ color: 'var(--cs-sidebar-text)' }}
                  data-testid="nav-games"
                >
                  <GameController size={18} style={gamesActive ? { color: '#EC4899', filter: 'drop-shadow(0 0 6px rgba(236,72,153,0.6))' } : {}} className="transition-all duration-200" />
                  <span className="transition-colors duration-200" style={gamesActive ? { color: '#EC4899' } : {}}>Games</span>
                  <CaretDown size={12} className={`transition-transform duration-300 ${showGamesMenu ? 'rotate-180' : ''}`} />
                  {gamesActive && (
                    <span className="nav-active-glow" style={{ background: '#EC4899', boxShadow: '0 0 12px rgba(236,72,153,0.8)' }} />
                  )}
                </button>
                <AnimatePresence>
                  {showGamesMenu && (
                    <motion.div
                      ref={gamesMenuRef}
                      initial={{ opacity: 0, y: 8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.98 }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute top-full right-0 mt-3 border rounded-xl shadow-2xl z-50 overflow-hidden w-56"
                      style={{ background: 'var(--cs-modal-bg)', borderColor: 'var(--cs-border)' }}
                      data-testid="games-dropdown"
                    >
                      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#EC4899]/30 to-transparent" />
                      <div className="p-2 space-y-1">
                        {gamesItems.map((item) => {
                          const isActive = location.pathname.startsWith(item.path);
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              onClick={() => setShowGamesMenu(false)}
                              className={`flex items-center gap-3 p-2.5 rounded-lg transition-all duration-200 ${isActive ? 'bg-[var(--cs-bg-hover)]' : 'hover:bg-[var(--cs-bg-hover)]'}`}
                              data-testid={`games-${item.label.toLowerCase()}`}
                            >
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: isActive ? `${item.color}18` : `${item.color}12` }}>
                                <item.icon size={16} style={{ color: item.color }} />
                              </div>
                              <div>
                                <div className="text-[13px] font-semibold" style={{ color: isActive ? item.color : 'var(--cs-text)', fontFamily: 'Outfit, sans-serif' }}>{item.label}</div>
                                <div className="text-[11px]" style={{ color: 'var(--cs-text-dim)' }}>{item.desc}</div>
                              </div>
                              {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: item.color, boxShadow: `0 0 6px ${item.color}60` }} />}
                            </NavLink>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            <div className="flex items-center gap-2">
              {/* Last Sync Time */}
              <div className="relative">
                <button
                  onClick={() => setShowSyncDetails(!showSyncDetails)}
                  className={`flex items-center gap-1.5 p-2 rounded-lg transition-all duration-200 group ${isSyncStale ? 'text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20' : 'text-[#10B981] hover:bg-white/5 border border-transparent'}`}
                  title="Data sync times"
                  data-testid="last-sync-btn"
                >
                  <ClockCounterClockwise size={18} className={`${isSyncStale ? 'text-[#EF4444]' : 'text-[#10B981]'}`} />
                  {isSyncStale && <div className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />}
                </button>

                {/* Dropdown with all sync details */}
                {showSyncDetails && (
                  <div className="fixed sm:absolute inset-x-3 sm:inset-x-auto top-16 sm:top-full sm:right-0 sm:mt-2 sm:w-80 border rounded-2xl shadow-2xl z-50 overflow-hidden sync-dropdown"
                    style={{ background: 'var(--cs-modal-bg)', borderColor: 'var(--cs-border)' }} data-testid="sync-details-dropdown">
                    {/* Glow accent */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#10B981]/40 to-transparent" />
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-8 bg-[#10B981]/8 blur-xl rounded-full" />
                    
                    {/* Header */}
                    <div className="relative px-4 py-3 border-b" style={{ borderColor: 'var(--cs-border)' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-[#10B981]/10 flex items-center justify-center border border-[#10B981]/20">
                            <ClockCounterClockwise size={14} weight="bold" className="text-[#10B981]" />
                          </div>
                          <span className="text-xs font-bold uppercase tracking-widest" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--cs-text)' }}>Sync Status</span>
                        </div>
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${isSyncStale ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' : 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20'}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${isSyncStale ? 'bg-[#EF4444] animate-pulse' : 'bg-[#10B981] sync-dot-pulse'}`} />
                          {isSyncStale ? 'Stale' : 'Fresh'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Sync entries */}
                    <div className="py-1.5 max-h-[60vh] overflow-y-auto">
                      {lastSync?.all_syncs?.length ? lastSync.all_syncs.map((s, i) => {
                        const isEntryStale = s.iso ? (new Date() - new Date(s.iso)) > 2 * 60 * 60 * 1000 : false;
                        return (
                          <div
                            key={s.name || i}
                            className="group flex items-center gap-3 px-4 py-2.5 mx-1.5 rounded-lg hover:bg-white/[0.03] transition-all duration-200 sync-entry"
                            style={{ animationDelay: `${i * 40}ms` }}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 transition-colors ${isEntryStale ? 'bg-[#EF4444]' : 'bg-[#10B981]/60 group-hover:bg-[#10B981]'}`} />
                            <span className="text-xs truncate flex-1 transition-colors" style={{ color: 'var(--cs-text-secondary)' }}>{s.name}</span>
                            <span className={`text-[11px] font-mono font-semibold whitespace-nowrap tabular-nums transition-colors ${isEntryStale ? 'text-[#EF4444]' : 'text-[#525252] group-hover:text-white'}`}>{s.time}</span>
                          </div>
                        );
                      }) : (
                        <div className="px-4 py-6 text-center">
                          <ClockCounterClockwise size={24} className="mx-auto text-[#525252] mb-2" />
                          <p className="text-xs text-[#525252]">No sync data yet</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Footer glow */}
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#262626] to-transparent" />
                  </div>
                )}
              </div>

              {canAccessSettings && (
              <button
                onClick={handleSettingsClick}
                className={`header-action-btn hidden p-2 rounded-xl transition-all duration-200 ${showSettings ? 'text-[#CD040B] bg-[#CD040B]/10 shadow-[0_0_15px_rgba(205,4,11,0.15)]' : 'hover:bg-[var(--cs-bg-hover)]'}`}
                style={!showSettings ? { color: 'var(--cs-text-muted)' } : {}}
                title="Settings"
                data-testid="settings-btn"
              >
                <GearSix size={20} className={showSettings ? 'animate-[spin_3s_linear_infinite]' : ''} />
              </button>
              )}
              <Suspense fallback={null}><div className="lg:hidden"><NotificationBell onOpen={() => setNotifOpen(true)} /></div></Suspense>
              <button
                onClick={toggleTheme}
                className="header-action-btn hidden p-2 rounded-xl transition-all duration-300 hover:bg-[var(--cs-bg-hover)]"
                style={{ color: 'var(--cs-text-muted)' }}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                data-testid="theme-toggle-btn"
              >
                {theme === 'dark' ? <Sun size={20} weight="bold" /> : <Moon size={20} weight="bold" />}
              </button>
              <button
                onClick={handleLogout}
                className="header-action-btn hidden items-center gap-2 px-3 py-2 text-sm font-medium transition-colors rounded-xl hover:bg-[var(--cs-bg-hover)]"
                style={{ color: 'var(--cs-text-muted)' }}
                data-testid="logout-btn"
              >
                <SignOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

      </header>

      {/* Edge-swipe catcher — native-app style left-edge swipe to open drawer */}
      <EdgeSwipeCatcher
        onOpen={() => setMobileDrawerOpen(true)}
        disabled={mobileDrawerOpen || showSettings}
      />

      {/* PWA install prompt — kicks in on 2nd visit, snoozes 30 days on dismiss */}
      <InstallPrompt />

      {/* Mobile Drawer — lazy-loaded with a shimmer shell so the drawer
          never looks "empty" during the chunk load on slow networks / post-deploy. */}
      <Suspense fallback={<MobileDrawerSkeleton open={mobileDrawerOpen} />}>
        <MobileDrawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          navItems={navItems}
          gamesItems={gamesItems}
          toolsCategories={toolsCategories}
          navColors={NAV_COLORS}
          palette={PALETTE}
          user={user}
          paletteTools={paletteTools}
          pins={pins}
          isPinned={isPinned}
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenSettings={handleSettingsClick}
          canAccessSettings={canAccessSettings}
          onLogout={handleLogout}
          onToggleTheme={toggleTheme}
          isDark={isDark}
        />
      </Suspense>

      {/* Bottom tab bar — mobile only.  Shows the 4 most-used destinations
          plus a "More" button that opens the existing slide-out drawer for
          everything else.  Hidden on md+ screens (≥768px) since desktop
          users have the top nav. */}
      <BottomTabBar
        slotPaths={slotPaths}
        navColors={NAV_COLORS}
        palette={PALETTE}
        paletteTools={paletteTools}
        onLongPressSlot={handleOpenSlotPicker}
      />
      <FavTabPicker
        open={activeSlotIdx !== null}
        onClose={() => setActiveSlotIdx(null)}
        tools={paletteTools}
        currentPath={activeSlotIdx != null ? slotPaths[activeSlotIdx] : null}
        slotIndex={activeSlotIdx}
        onSelect={handleSelectSlot}
      />

      {/* Settings Modal */}
      {showSettings && (() => {
        const ALL_SETTINGS_TABS = [
          { key: 'whitelist', label: 'Whitelist', icon: ShieldCheck, color: '#6366F1', desc: 'Access control' },
          { key: 'sheets', label: 'Reports', icon: Table, color: '#CD040B', desc: 'Import & configure' },
          { key: 'announcements', label: 'Announcements', icon: Megaphone, color: '#F59E0B', desc: 'Team updates' },
          { key: 'top10', label: 'Top 10', icon: Trophy, color: '#A855F7', desc: 'Leaderboard config' },
          { key: 'aliases', label: 'Store Aliases', icon: Storefront, color: '#10B981', desc: 'Name mapping' },
          { key: 'goalAliases', label: 'Goals Alias', icon: Target, color: '#F97316', desc: 'Column labels' },
          { key: 'goalTargets', label: 'Goals', icon: Target, color: '#EF4444', desc: 'Targets & PBI' },
          { key: 'cardDisplay', label: 'Card Display', icon: Palette, color: '#EC4899', desc: 'Visual layout' },
          { key: 'pbi', label: 'PBI Reports', icon: ChartBar, color: '#F59E0B', desc: 'Report fields' },
          { key: 'locations', label: 'Locations', icon: MapPin, color: '#3B82F6', desc: 'Store locations' },
          { key: 'contacts', label: 'Contact Directory', icon: AddressBook, color: '#06B6D4', desc: 'Phone numbers' },
          { key: 'perks', label: 'Perks', icon: Lightning, color: '#F59E0B', desc: 'Streaming bundles' },
          { key: 'reps', label: 'Reps', icon: UserList, color: '#F97316', desc: 'Hide/show reps' },
          { key: 'stores', label: 'Stores', icon: MapPin, color: '#3B82F6', desc: 'Hide/show stores' },
          { key: 'coaching', label: 'Coaching', icon: Chalkboard, color: '#CD040B', desc: 'Areas & sources' },
          { key: 'calendars', label: 'Calendars', icon: CalendarBlank, color: '#10B981', desc: 'Google calendars' },
          { key: 'vhdp', label: 'VHDP', icon: ShieldCheck, color: '#CD040B', desc: 'Device values' },
          { key: 'paychecks', label: 'Commission Estimator', icon: CurrencyDollar, color: '#10B981', desc: 'Comp mapping' },
          { key: 'profiles', label: 'Profiles', icon: Users, color: '#A855F7', desc: 'Profile layout' },
          { key: 'inventory', label: 'Inventory Vault', icon: Cube, color: '#8B5CF6', desc: 'Sheet config' },
          { key: 'reconciliation', label: 'Reconciliation', icon: Scales, color: '#0EA5E9', desc: 'Report imports' },
          { key: 'notifications', label: 'Notifications', icon: BellRinging, color: '#A855F7', desc: 'Push alerts' },
          { key: 'snapshots', label: 'Snapshots', icon: Camera, color: '#06B6D4', desc: 'Nightly archive' },
          { key: 'onboarding', label: 'Onboarding', icon: ClipboardText, color: '#10B981', desc: 'Applications inbox' },
          { key: 'dailies', label: 'Dailies', icon: Vault, color: '#10B981', desc: 'Daily cash logs' },
          { key: 'pto', label: 'Time Off', icon: Umbrella, color: '#06B6D4', desc: 'Review PTO requests' },
          { key: 'qrReviews', label: 'Review QRs', icon: QrCode, color: '#CD040B', desc: 'Per-store Google review PDFs' },
        ];
        const userSections = user?.settings_sections || [];
        // Snapshot manager + Onboarding are admin-gated on the backend and
        // surfaced to every admin automatically — no need to add them
        // manually to `settings_sections`.
        const isAdmin = user?.role === 'admin' || user?.is_manager;
        const effectiveSections = isAdmin ? [...userSections, 'snapshots', 'onboarding', 'dailies', 'pto', 'contacts', 'qrReviews'] : userSections;
        const SETTINGS_TABS = ALL_SETTINGS_TABS.filter(t => effectiveSections.includes(t.key));
        // If the user has access to Whitelist, that is now the default landing
        // tab for Settings — admins manage permissions there most often.
        // Otherwise fall back to the first tab they have access to.
        const defaultTab = SETTINGS_TABS.find(t => t.key === 'whitelist') || SETTINGS_TABS[0];
        const activeTab = SETTINGS_TABS.find(t => t.key === settingsTab) || defaultTab;

        const SETTINGS_COMPONENTS = {
          sheets: <SheetsManager onDataChange={() => setRefreshKey(k => k + 1)} />,
          announcements: <AnnouncementsManager />,
          top10: <Top10Manager />,
          aliases: <StoreAliasManager />,
          goalAliases: <GoalAliasManager />,
          goalTargets: <GoalsManager />,
          cardDisplay: <CardDisplaySettings />,
          pbi: <PBIManager />,
          locations: <LocationsSettings />,
          contacts: <ContactSettings />,
          perks: <PerksSettings />,
          whitelist: <WhitelistManager />,
          reps: <RepsSettings />,
          stores: <StoresSettings />,
          coaching: <CoachingSettings />,
          calendars: <CalendarSettings />,
          vhdp: <VHDPSettings />,
          paychecks: <PaycheckSettings />,
          profiles: <ProfileSettings />,
          inventory: <InventoryVaultSettings />,
          reconciliation: <ReconciliationSettings />,
          notifications: <NotificationSettings />,
          snapshots: <SnapshotManager />,
          onboarding: <OnboardingSubmissions />,
          dailies: <DailiesSettings />,
          pto: <PTOSettings />,
          qrReviews: <QRReviewsSettings />,
        };

        return (
        <div className="fixed inset-0 z-50 sm:backdrop-blur-sm flex items-center justify-center sm:p-4 modal-overlay" style={{ background: 'var(--cs-bg, var(--cs-modal-overlay))' }} onClick={closeSettings}>
          <div className="sm:border sm:rounded-2xl w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-6xl overflow-hidden flex flex-col sm:shadow-2xl modal-content" style={{ background: 'var(--cs-bg, var(--cs-modal-bg))', borderColor: 'var(--cs-border)', touchAction: 'pan-y' }} onClick={e => e.stopPropagation()}>

            {/* === MOBILE HEADER (page-style with Back button) === */}
            <div className="flex sm:hidden items-center gap-2 px-3 py-3 border-b shrink-0" style={{ borderColor: 'var(--cs-border)' }}>
              <button
                onClick={closeSettings}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl text-sm font-bold shrink-0 transition-all active:scale-95"
                style={{ background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)', color: 'var(--cs-text)' }}
                data-testid="close-settings-mobile"
                aria-label="Back"
              >
                <ArrowLeft size={16} weight="bold" />
                <span>Back</span>
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] truncate" style={{ color: 'var(--cs-text-muted)' }}>Settings</div>
                <div className="text-base font-black truncate leading-tight" style={{ color: 'var(--cs-text)', fontFamily: 'Outfit, sans-serif' }}>{activeTab.label}</div>
              </div>
              <button
                onClick={() => setSettingsSidebarOpen(true)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl shrink-0 transition-all active:scale-95"
                style={{ background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)', color: 'var(--cs-text)' }}
                data-testid="settings-sidebar-toggle"
                aria-label="Switch section"
              >
                <List size={18} weight="bold" />
              </button>
            </div>

            {/* === DESKTOP HEADER === */}
            <div className="hidden sm:flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--cs-border)' }}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSettingsSidebarOpen(true)}
                  className="p-2 -ml-2 rounded-lg transition-colors hover:bg-[var(--cs-bg-hover)]"
                  style={{ color: 'var(--cs-text-muted)' }}
                  data-testid="settings-sidebar-toggle-desktop"
                  aria-label="Open settings menu"
                >
                  <List size={20} />
                </button>
                <div className="w-10 h-10 rounded-xl bg-[#CD040B]/10 flex items-center justify-center border border-[#CD040B]/20">
                  <GearSix size={18} className="text-[#CD040B]" />
                </div>
                <div className="flex flex-col">
                  <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--cs-text)' }}>Settings</h2>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <activeTab.icon size={11} weight="fill" style={{ color: activeTab.color }} />
                    <span className="text-[11px] font-medium" style={{ color: 'var(--cs-text-muted)' }}>{activeTab.label}</span>
                  </div>
                </div>
              </div>
              <button onClick={closeSettings} className="p-2 rounded-lg transition-colors hover:bg-[var(--cs-bg-hover)]" style={{ color: 'var(--cs-text-muted)' }} data-testid="close-settings">
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden relative">
              <div className="flex flex-col h-full sm:h-[calc(90vh-73px)]">

                {/* === UNIFIED SLIDE-OUT SIDEBAR (mobile + desktop) === */}
                <AnimatePresence>
                  {settingsSidebarOpen && (
                    <>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="hidden sm:block fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
                        onClick={() => setSettingsSidebarOpen(false)}
                        data-testid="settings-sidebar-overlay"
                      />
                      <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed top-0 left-0 bottom-0 w-full sm:w-[82%] sm:max-w-[320px] z-[61] sm:border-r overflow-y-auto shadow-2xl"
                        style={{ background: 'var(--cs-bg, var(--cs-modal-bg))', borderColor: 'var(--cs-border)', touchAction: 'pan-y' }}
                        data-testid="settings-sidebar-drawer"
                      >
                        {/* Sidebar header — mobile gets a full app-bar */}
                        <div className="flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--cs-border)', background: 'var(--cs-bg, var(--cs-modal-bg))' }}>
                          <button
                            onClick={() => setSettingsSidebarOpen(false)}
                            className="sm:hidden inline-flex items-center gap-1.5 h-10 px-3 rounded-xl text-sm font-bold transition-all active:scale-95"
                            style={{ background: 'var(--cs-bg-surface)', border: '1px solid var(--cs-border)', color: 'var(--cs-text)' }}
                            data-testid="settings-sidebar-close-mobile"
                            aria-label="Close settings"
                          >
                            <ArrowLeft size={16} weight="bold" />
                            <span>Back</span>
                          </button>
                          <div className="flex items-center gap-2.5">
                            <div className="hidden sm:flex w-8 h-8 rounded-lg bg-[#CD040B]/10 items-center justify-center border border-[#CD040B]/20">
                              <GearSix size={16} className="text-[#CD040B]" />
                            </div>
                            <h3 className="text-base sm:text-sm font-black sm:font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--cs-text)' }}>Settings</h3>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" style={{ background: 'var(--cs-bg-hover)', color: 'var(--cs-text-muted)' }}>{SETTINGS_TABS.length}</span>
                          </div>
                          <button
                            onClick={() => setSettingsSidebarOpen(false)}
                            className="hidden sm:inline-flex p-1.5 rounded-lg hover:bg-[var(--cs-bg-hover)] transition-colors"
                            style={{ color: 'var(--cs-text-muted)' }}
                            data-testid="settings-sidebar-close"
                            aria-label="Close menu"
                          >
                            <X size={18} />
                          </button>
                        </div>

                        {/* Sidebar nav items — bigger tap targets on mobile */}
                        <div className="px-2 py-3 space-y-0.5">
                          {SETTINGS_TABS.map(({ key, label, icon: Icon, color, desc }) => (
                            <button
                              key={key}
                              onClick={() => { setSettingsTab(key); setSettingsSidebarOpen(false); }}
                              className={`w-full flex items-center gap-3 px-3 py-3 sm:py-2.5 rounded-xl text-left transition-all duration-200 active:scale-[0.99] ${activeTab?.key === key ? 'bg-[var(--cs-bg-hover)]' : 'hover:bg-[var(--cs-bg-hover)]'}`}
                              data-testid={`settings-tab-${key}`}
                            >
                              <div className="w-9 h-9 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: activeTab?.key === key ? `${color}18` : `${color}08` }}>
                                <Icon size={18} weight={activeTab?.key === key ? 'fill' : 'regular'} style={{ color: activeTab?.key === key ? color : 'var(--cs-text-muted)' }} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm sm:text-[13px] font-bold sm:font-medium truncate" style={{ color: activeTab?.key === key ? 'var(--cs-text)' : 'var(--cs-text-secondary)' }}>{label}</div>
                                <div className="text-[11px] sm:text-[10px] truncate" style={{ color: 'var(--cs-text-dim)' }}>{desc}</div>
                              </div>
                              {activeTab?.key === key && <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color, boxShadow: `0 0 6px ${color}60` }} />}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>

                {/* === CONTENT AREA === */}
                <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-6" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                  {SETTINGS_COMPONENTS[(activeTab && activeTab.key) || settingsTab] || null}
                </div>

              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" key={refreshKey}>
        <RouteErrorBoundary routeLabel={location.pathname} key={location.pathname}>
        <Suspense fallback={<RouteSkeleton variant="page" />}>
        <div data-route-content key={location.pathname} className="cs-route-fade">
        <Routes>
          {/* TEMPORARILY DISABLED: the new role-based MyHome / ManagerHome
           * landing page (Hero card, TabBar, team rollup, etc.) is hidden
           * per product decision — the old announcements + widgets dashboard
           * is the login-landing for now.  All MyHome code remains in place
           * (just unrouted) and can be re-enabled by restoring this route:
           *     <Route path="/me" element={<MyHome />} />
           *     <Route path="/" element={<Navigate to="/me" replace />} />
           */}
          <Route path="/" element={<HomeDashboard />} />
          <Route path="/me" element={<HomeDashboard />} />
          <Route path="/dashboard-legacy" element={<HomeDashboard />} />
          <Route path="/stores" element={<StoresView />} />
          <Route path="/reps" element={<RepsView />} />
          <Route path="/profitability" element={<ProfitabilityMatrix />} />
          <Route path="/device-preview" element={<DeviceSpotlightPreview />} />
          <Route path="/top10" element={<Top10View />} />
          <Route path="/goals" element={<GoalsView />} />
          <Route path="/state-of-business" element={<StateOfBusiness />} />
          <Route path="/plans" element={<PlansCalculator />} />
          <Route path="/bmsm" element={<BMSMPromos />} />
          <Route path="/porting" element={<PortingDirectory />} />
          <Route path="/locations" element={<LocationsView />} />
          <Route path="/perks" element={<PerksView />} />
          <Route path="/vhdp" element={<VHDPCalculator />} />
          <Route path="/vhi-closer" element={<VHIClosing />} />
          <Route path="/forward" element={<Forward />} />
          <Route path="/paychecks" element={<PaycheckView />} />
          <Route path="/rep/:name" element={<RepProfile />} />
          <Route path="/store/:name" element={<StoreProfile />} />
          <Route path="/coaching" element={<CoachingDashboard />} />
          <Route path="/calendars" element={<CalendarView />} />
          <Route path="/inventory" element={<InventoryVault />} />
          <Route path="/reconciliation" element={<Reconciliation />} />
          <Route path="/sales-pitches" element={<SalesPitches />} />
          <Route path="/call-scripts" element={<CallScripts />} />
          <Route path="/notes" element={<NotesView />} />
          <Route path="/contacts" element={<ContactDirectory />} />
          <Route path="/qr-reviews" element={<QRReviewsView />} />
          <Route path="/verizon-news" element={<VerizonNews />} />
          <Route path="/simplicity-hub" element={<SimplicityHub />} />
          <Route path="/dailies" element={<DailiesView />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/pto" element={<PTORequest />} />
          <Route path="/handbook" element={<CompanyHandbook />} />
          <Route path="/signal-check" element={<SignalCheck />} />
          <Route path="/business-funnel" element={<BusinessFunnel />} />
          <Route path="/buy-phones" element={<BuyPhones />} />
          <Route path="/battleship" element={<BattleshipLanding />} />
          <Route path="/battleship/game/:boardId" element={<BattleshipGameBoard />} />
          <Route path="/battleship/fire" element={<BattleshipFireShot />} />
          <Route path="/battleship/fire/:boardId" element={<BattleshipFireShot />} />
          <Route path="/battleship/team/:teamId" element={<BattleshipTeamPortal />} />
          <Route path="/battleship/admin" element={<BattleshipAdmin />} />
          <Route path="/battleship/leaderboard" element={<BattleshipLeaderboard />} />
          <Route path="/battleship/archives" element={<BattleshipArchives />} />
          <Route path="/bingo" element={<BingoHome />} />
          <Route path="/bingo/admin" element={<BingoAdmin />} />
          <Route path="/bingo/history" element={<BingoHistory />} />
          <Route path="/bingo/archives" element={<BingoArchives />} />
          <Route path="/conquest" element={<ConquestHome />} />
          <Route path="/conquest/admin" element={<ConquestAdmin />} />
          <Route path="/conquest/history" element={<ConquestHistory />} />
          <Route path="/ringer" element={<RingerHome />} />
          <Route path="/ringer/admin" element={<RingerAdmin />} />
          <Route path="/today" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
        </Suspense>
        </RouteErrorBoundary>
      </main>
      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        tools={paletteTools}
        recents={recents}
        pins={pins}
        isPinned={isPinned}
        togglePin={togglePin}
      />
    </div>
  );
};

export default Dashboard;
